/**
 * Faithful TS port of the Motionly SDK's assembly of the `oguryMotionly`
 * creative-settings object (`ogury-callbacks-settingsCallbacks.js`). This is
 * the prerequisite for every engine-fired track: the `/ocs` payload reads
 * `oguryMotionly.*` (templateId, templateVersion, experienceId, siteId,
 * containerPolicy, macros, tagVersion, clickStrategy, …), so these settings
 * must be assembled exactly as the SDK does.
 *
 * The SDK mutates one `oguryMotionly` object across two lifecycle phases:
 *   1. Params ingestion at `setDomRoot` (`:1377-1473`): from `motionly-params`.
 *   2. `loadedWorkflow.onRequested` (`:474-545`): from the experience metadata.
 * Here those phases are pure functions composed in order (defaults → params →
 * metadata). All side-effecting reads are injected so the assembly is
 * deterministic.
 *
 * Divergences (all behaviour-neutral on the assembled object):
 *  (a) Side-effects injected (parent reads, decode, deviceOS, disable flag).
 *  (b) Immutable: each phase returns a new settings object instead of mutating.
 *  (c) Diagnostic `logConsole` calls dropped — they never affect the assembled
 *      settings.
 *  (d) `extractClickStrategyFromParentParameters` is read once and reused.
 */

import { DeviceOS } from './types';
export { DeviceOS };

/** A landing/click entry from `metadata.tracking.clicks`. */
export interface LandingClick {
  id?: string | number;
  url?: string;
  type?: string;
  deviceOS?: string;
}

/** A calendar destination entry from `metadata.tracking.calendarDestinationUrls`. */
export interface CalendarDestinationUrl {
  calendarType?: string;
  url?: string;
}

/** `motionly-params.variables` — the OCS "Third-Party Tag" payload. */
export interface MotionlyParamsVariables {
  impressionTag?: string;
  clickCommand?: string;
  clickUrl?: string;
  tag_version?: string | number;
  ratio?: number;
  events?: Record<string, string | undefined>;
  macros?: Record<string, string | undefined>;
  units?: Record<string, { ratio?: number } | undefined>;
  [key: string]: unknown;
}

/** Parsed `motionly-params` JSON. */
export interface MotionlyParams {
  experienceId?: string;
  siteId?: string;
  clickStrategy?: string;
  eventMedia?: string;
  isVpaidCreative?: boolean;
  variables?: MotionlyParamsVariables;
}

/** The experience metadata handed to `loadedWorkflow.onRequested`. */
export interface ExperienceMetadata {
  templateId?: string;
  templateVersion?: string | number;
  tracking?: {
    clicks?: LandingClick[];
    calendarDestinationUrls?: CalendarDestinationUrl[];
  };
  containerPolicy?: string;
  smartcommerce?: boolean;
  ignoreContainerClicks?: boolean;
}

/** The experience identifiers handed to `onRequested`. */
export interface ExperienceIds {
  experienceId?: string;
}

/**
 * The full assembled creative settings. Superset of the read-only subsets used
 * by `events-manager.ts` (`OguryMotionlySettings`) and `click-logic.ts`
 * (`OguryMotionlyClickSettings`); their structural unification is the recorded
 * post-parity click-consolidation follow-up.
 */
export interface OguryMotionly {
  disableEvents: boolean;
  impressionTag?: string;
  clickCommand?: string;
  landingUrlOCS?: string;
  clickUrl?: string;
  mraidUrl?: string;
  events: Record<string, string | undefined>;
  variables?: MotionlyParamsVariables;
  experienceId?: string;
  siteId?: string;
  templateId?: string;
  templateVersion?: string | number;
  experienceRatio?: number;
  containerPolicy: string;
  isSmartCommerce: boolean;
  ignoreContainerClicks: boolean;
  isVpaidCreative: boolean;
  clickStrategy: string;
  macroSource: string;
  eventMedia: string;
  landingUrls?: LandingClick[];
  tagVersion?: string | number;
  macros?: Record<string, string | undefined>;
}

/** Injected reads for the params-ingestion phase (`setDomRoot`). */
export interface SettingsParamsEnvironment {
  /** `adUnitType` (`extractOguryAdUnit` result); undefined under third-party. */
  adUnitType: string | undefined;
  /** `canAccessParent` (`hasParentAndCanAccess(window)`). */
  canAccessParent: boolean;
  /** `parent.parameters["click_url"]` — the Admin click-command. */
  getParentClickUrl(): string | undefined;
  /** `extractClickStrategyFromParentParameters` → `parent.parameters.click_strategy`. */
  getParentClickStrategy(): string | undefined;
  /** `extractParameterFromParentContext(window, canAccessParent, name)`. */
  extractParentParameter(name: string): string | undefined;
  /** `atob` — decodes the base64 impression tag. */
  decodeBase64(value: string): string;
  /** preview || demo || `ocsInstrument.disableEvents` → force-disable analytics. */
  forceDisableEvents: boolean;
}

/** Injected reads for the `onRequested` metadata phase. */
export interface SettingsMetadataEnvironment {
  /** `resolveDeviceOS()` — for calendar-landing selection. */
  resolveDeviceOS(): DeviceOS;
}

/** Supported container policies (`:28-34`). */
const CONTAINER_POLICIES: Record<string, string | undefined> = {
  fit: 'fit',
  fitWidth: 'fitWidth',
  fitHeight: 'fitHeight',
  crop: 'crop'
};

/** `defaultContainerPolicy` (`:40`). */
const DEFAULT_CONTAINER_POLICY = CONTAINER_POLICIES.fit as string;

/**
 * `applyNewContainerPolicy`: hardcoded `true` in the SDK (the legacy path is
 * dead pending its TODO removal); reproduced as a constant.
 */
const APPLY_NEW_CONTAINER_POLICY = true;

/** `macroToParentMapping`: macro name → parent-context param. */
const MACRO_TO_PARENT_MAPPING: Record<string, string> = {
  campaignid: 'campaignId',
  advert_id: 'impressionId',
  creative_id: 'creativeId',
  apikey: 'apiKey',
  page: 'page',
  app_bundle: 'appBundle',
  sdk: 'sdkVersion',
  external_app_exchange: '_appExchange'
};

/** A macro left unreplaced by the server still looks like `[macroKey]`. */
export function isUnreplacedMacro(value: unknown, key: string): boolean {
  return typeof value === 'string' && value === '[' + key + ']';
}

/**
 * The creative-settings defaults, before any params or metadata.
 */
export function createDefaultSettings(): OguryMotionly {
  return {
    disableEvents: false,
    impressionTag: undefined,
    clickCommand: undefined,
    landingUrlOCS: undefined,
    clickUrl: undefined,
    mraidUrl: undefined,
    events: {},
    variables: undefined,
    experienceId: undefined,
    siteId: undefined,
    templateId: undefined,
    templateVersion: undefined,
    experienceRatio: undefined,
    containerPolicy: DEFAULT_CONTAINER_POLICY,
    isSmartCommerce: false,
    ignoreContainerClicks: false,
    isVpaidCreative: false,
    clickStrategy: 'parallel',
    macroSource: 'tag',
    eventMedia: 'api'
  };
}

/**
 * Phase 1 — params ingestion (`setDomRoot`). Applies the parsed
 * `motionly-params` (and the parent-context-derived values) onto the settings.
 * Returns a new object.
 */
export function applyParams(
  base: OguryMotionly,
  params: MotionlyParams,
  environment: SettingsParamsEnvironment
): OguryMotionly {
  const settings: OguryMotionly = { ...base };

  settings.experienceId = params.experienceId;
  settings.siteId = params.siteId;

  if (params.clickStrategy !== undefined) {
    settings.clickStrategy = params.clickStrategy;
  } else {
    const parentClickStrategy = environment.getParentClickStrategy();
    if (parentClickStrategy !== undefined) {
      settings.clickStrategy = parentClickStrategy;
    }
  }

  if (params.eventMedia !== undefined) {
    settings.eventMedia = params.eventMedia;
  }
  if (params.isVpaidCreative !== undefined) {
    settings.isVpaidCreative = params.isVpaidCreative;
  }

  const variables = params.variables;
  if (variables !== undefined) {
    settings.variables = variables;

    let unit: { ratio?: number } | undefined;
    if (settings.experienceId !== undefined) {
      unit = variables;
    } else if (
      environment.adUnitType !== undefined &&
      variables.units !== undefined
    ) {
      unit = variables.units[environment.adUnitType];
    }
    if (unit !== undefined) {
      settings.experienceRatio = unit.ratio;
    }

    settings.impressionTag =
      variables.impressionTag === undefined
        ? undefined
        : environment.decodeBase64(variables.impressionTag);
    settings.clickCommand = variables.clickCommand;
    settings.clickUrl = variables.clickUrl;
    settings.tagVersion = variables.tag_version;
    // The Admin "click-command" is used for the landing page / redirect.
    settings.mraidUrl =
      environment.adUnitType !== undefined &&
      environment.canAccessParent === true
        ? environment.getParentClickUrl()
        : undefined;
    settings.events = variables.events || {};
    settings.macros = variables.macros;

    // Resolve macros the server left unreplaced (still `[macroKey]`) from the
    // parent context. Mutates the macros bag (exactly as the SDK does) and
    // flips macroSource to "parent" once any replacement happens.
    if (
      settings.macros !== undefined &&
      typeof settings.macros === 'object' &&
      environment.canAccessParent === true
    ) {
      const macros = settings.macros;
      let hasReplacedMacros = false;
      for (const macroKey in MACRO_TO_PARENT_MAPPING) {
        if (Object.prototype.hasOwnProperty.call(macros, macroKey)) {
          const macroValue = macros[macroKey];
          if (isUnreplacedMacro(macroValue, macroKey)) {
            const extractedValue = environment.extractParentParameter(
              MACRO_TO_PARENT_MAPPING[macroKey]
            );
            if (extractedValue !== undefined) {
              macros[macroKey] = extractedValue;
              hasReplacedMacros = true;
            }
          }
        }
      }
      if (hasReplacedMacros === true) {
        settings.macroSource = 'parent';
      }
    }
  }

  if (environment.forceDisableEvents === true) {
    settings.disableEvents = true;
  }

  return settings;
}

/**
 * Phase 2 — `loadedWorkflow.onRequested`. Applies the experience metadata
 * (template, landing/calendar URLs, container policy, smart-commerce) and
 * backfills the experienceId. Returns a new object.
 */
export function applyMetadata(
  base: OguryMotionly,
  experienceIds: ExperienceIds,
  metadata: ExperienceMetadata | undefined,
  environment: SettingsMetadataEnvironment
): OguryMotionly {
  const settings: OguryMotionly = { ...base };

  if (metadata !== undefined) {
    // Guarded like every neighbouring field below: in the engine-native boot the
    // entry point hydrates the template from the fetched experience, then the
    // engine's own `Request` re-enters here with a metadata-less context
    // (`onRequested` substitutes `{}`). An unconditional assignment wiped the
    // template for every later event.
    if (metadata.templateId !== undefined) {
      settings.templateId = metadata.templateId;
    }
    if (metadata.templateVersion !== undefined) {
      settings.templateVersion = metadata.templateVersion;
    }

    const tracking = metadata.tracking;
    if (
      tracking !== undefined &&
      tracking.clicks !== undefined &&
      tracking.clicks.length > 0
    ) {
      if (tracking.clicks.length > 1) {
        settings.landingUrls = tracking.clicks;
      }
      const firstUrl = tracking.clicks[0].url;
      if (firstUrl !== undefined && firstUrl !== null && firstUrl.length > 0) {
        settings.landingUrlOCS = firstUrl;
        settings.landingUrls = tracking.clicks;
      }
    }

    if (
      tracking !== undefined &&
      tracking.calendarDestinationUrls !== undefined &&
      Array.isArray(tracking.calendarDestinationUrls) &&
      tracking.calendarDestinationUrls.length > 0
    ) {
      const deviceOS = environment.resolveDeviceOS();
      const calendarType = deviceOS === 'ios' ? 'IOS' : 'ANDROID';
      // Faithful: the matched entry's url is taken raw and the guard is only
      // `!== null`. A matched entry whose url is undefined therefore clobbers
      // landingUrlOCS to undefined — reproduced, not guarded.
      let calendarUrl: string | null | undefined = null;
      for (let i = 0; i < tracking.calendarDestinationUrls.length; i++) {
        if (tracking.calendarDestinationUrls[i].calendarType === calendarType) {
          calendarUrl = tracking.calendarDestinationUrls[i].url;
          break;
        }
      }
      if (calendarUrl !== null) {
        settings.landingUrlOCS = calendarUrl;
      }
    }

    let newContainerPolicy = metadata.containerPolicy;
    if (
      APPLY_NEW_CONTAINER_POLICY === true &&
      newContainerPolicy !== undefined
    ) {
      // An unsupported server policy falls back to the default one.
      if (CONTAINER_POLICIES[newContainerPolicy] === undefined) {
        newContainerPolicy = DEFAULT_CONTAINER_POLICY;
      }
      settings.containerPolicy = newContainerPolicy;
    }
    if (metadata.smartcommerce === true) {
      settings.isSmartCommerce = true;
    }
    if (metadata.ignoreContainerClicks === true) {
      settings.ignoreContainerClicks = true;
    }
  }

  if (settings.experienceId === undefined) {
    // Backfill so both siteId and experienceId reach the events reporting; it
    // does not affect the experience loading.
    settings.experienceId = experienceIds.experienceId;
  }

  return settings;
}
